import { Component } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { CuttingToolType } from "@enums/cuttingToolType.enum";
import { MaterialType } from "@enums/materialType.enum";
import { RoughFinish } from "@enums/roughFinish.enum";
import { CuttingTool } from "@interfaces/models/cuttingTool.interface";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { ProductionToolProgram } from "@interfaces/models/productionToolProgram.interface";
import { ProductionToolProgramLine } from "@interfaces/models/productionToolProgramLine.interface";
import { ToolHolder } from "@interfaces/models/toolHolder.interface";
import { CuttingToolService } from "@services/cuttingTool.service";
import { OperationService } from "@services/operation.service";
import { ProductionToolProgramService } from "@services/productionToolProgram.service";
import { ProductionToolProgramLineService } from "@services/productionToolProgramLine.service";
import { ToolHolderService } from "@services/toolHolder.service";
import { ToolOrderService } from "@services/toolOrder.service";
import { forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";

@Component({
	moduleId: module.id,
	selector: "productionToolPrograms-print",
	styleUrls: ["productionToolPrograms-print.css"],
	templateUrl: "productionToolPrograms-print.html"
})
export class ProductionToolProgramsPrintAdminComponent {
	billetNames = "";
	cuttingTools: CuttingTool[] = [];

	get finishingCount(): number {
		return this.lines.filter((l) => l.roughFinish != null && l.roughFinish !== RoughFinish.Roughing).length;
	}

	item: ProductionToolProgram = new ProductionToolProgram();
	itemRelations: any = {};
	lines: ProductionToolProgramLine[] = [];
	loaded = false;

	get materialName(): string {
		return GetPropertiesFromEnum(MaterialType).find((m: any) => m.id === this.item.materialType)?.name || "";
	}

	operationLabel = "";

	// The operator is set per cutting tool; normally they all share one. First and last name only - no title.
	get operatorNames(): string {
		return Array.from(new Set(this.lines.map((l: any) => this.personName(l.operatorUserProfile)).filter(Boolean))).join(", ");
	}

	// Line order, 1 first, whatever order the grid shows them in on screen.
	get orderedLines(): ProductionToolProgramLine[] {
		return [...this.lines].sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0));
	}

	get partImageUrl(): string {
		return this.itemRelations.part?.tileImageUrl || "";
	}

	printedAt = new Date();

	get programmerName(): string {
		return this.personName(this.itemRelations.programmerUserProfile);
	}

	// Program ids are shown to four digits: 3 prints as 0003.
	get programNumber(): string {
		return this.item.id ? String(this.item.id).padStart(4, "0") : "";
	}

	get roughingCount(): number {
		return this.lines.filter((l) => l.roughFinish === RoughFinish.Roughing).length;
	}

	get toolImageUrl(): string {
		try {
			const images = JSON.parse(this.itemRelations.productionTool?.imageURLs || "[]");
			return (Array.isArray(images) ? images : []).find((i: any) => i?.fileUrl)?.fileUrl || "";
		} catch (e) {
			return "";
		}
	}

	toolHolders: ToolHolder[] = [];

	get totalRunTime(): string {
		return this.formatRunTime(this.lines.reduce((sum, l) => sum + (l.runTime || 0), 0));
	}

	constructor(
		private route: ActivatedRoute,
		private itemService: ProductionToolProgramService,
		private lineService: ProductionToolProgramLineService,
		private cuttingToolService: CuttingToolService,
		private toolHolderService: ToolHolderService,
		private operationService: OperationService,
		private toolOrderService: ToolOrderService
	) {
		this.loadReferenceData();
		this.route.params.subscribe((params) => { if (params.id) this.load(+params.id); });
	}

	cuttingToolFor(line: ProductionToolProgramLine): CuttingTool | undefined {
		return this.cuttingTools.find((t) => t.id === line.cuttingToolId);
	}

	doPrint() { window.print(); }

	formatRunTime(value?: number): string {
		const total = value || 0;
		if (!total) { return ""; }
		const hours = Math.floor(total / 60);
		const minutes = total % 60;
		return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
	}

	// A line can override the cutting tool's type, so print the line's where it has one.
	lineToolTypeName(line: ProductionToolProgramLine): string {
		if (line.toolType != null) { return CuttingToolType[line.toolType] || ""; }
		return this.toolTypeName(this.cuttingToolFor(line));
	}

	// Everything the sheet shows is loaded before it prints, so the dialog never opens on a half-filled page.
	// The operation and billets only add context, so either failing still prints the sheet.
	load(id: number) {
		this.itemService.getWithRelations(id).subscribe((res: any) => {
			this.item = res.response;
			this.itemRelations = res.response;

			const operation$ = this.item.operationId
				? this.operationService.getSingleById(this.item.operationId).pipe(catchError(() => of(null)))
				: of(null);

			forkJoin([this.lineService.forProgram(id), operation$]).subscribe(([lineRes, operationRes]: any[]) => {
				this.lines = lineRes.response;

				const operation = operationRes?.response;
				if (operation) {
					this.operationLabel = `OP${(operation.stageNumber || 0) * 10}${operation.processDescription ? " " + operation.processDescription : ""}`;
				}

				// The billets it's linked to, or failing that the billet its operation belongs to. This endpoint
				// returns the program itself, so the links are ProgramBlocks, not the DTO's BlockToolOrderIds.
				const linked: number[] = (res.response.programBlocks || []).map((b: any) => b.toolOrderId);
				const billetIds = linked.length > 0 ? linked : (operation?.toolOrderId ? [operation.toolOrderId] : []);
				const billets$ = billetIds.length > 0
					? forkJoin(billetIds.map((billetId) => this.toolOrderService.getSingleById(billetId).pipe(catchError(() => of(null)))))
					: of([]);

				billets$.subscribe((billetRes: any[]) => {
					this.billetNames = (billetRes || []).map((b: any) => b?.response && (b.response.name || "Billet " + b.response.id)).filter(Boolean).join(", ");
					this.loaded = true;
					this.printWhenReady();
				});
			});
		});
	}

	loadReferenceData() {
		this.cuttingToolService.getAll().subscribe((res: any) => this.cuttingTools = res.response.items);
		this.toolHolderService.getAll().subscribe((res: any) => this.toolHolders = res.response.items);
	}

	// Opens the print dialog once the fonts and every image on the sheet have loaded, so the logo and pictures
	// are never missing from the printout. Gives up waiting after a few seconds rather than never printing.
	personName(profile: any): string {
		return profile ? `${profile.firstName || ""} ${profile.lastName || ""}`.trim() : "";
	}

	printWhenReady() {
		setTimeout(() => {
			const images = Array.from(document.querySelectorAll(".sheet img")) as HTMLImageElement[];
			const imagesLoaded = images.map((img) => img.complete ? Promise.resolve() : new Promise<void>((resolve) => {
				img.addEventListener("load", () => resolve(), { once: true });
				img.addEventListener("error", () => resolve(), { once: true });
			}));
			const fontsLoaded = (document as any).fonts?.ready ?? Promise.resolve();
			const giveUp = new Promise<void>((resolve) => setTimeout(resolve, 4000));

			Promise.race([Promise.all([fontsLoaded, ...imagesLoaded]), giveUp]).then(() => window.print());
		});
	}

	rfName(rf?: RoughFinish): string {
		if (rf == null) return "";
		return rf === RoughFinish.Roughing ? "R" : "F";
	}

	toolHolderFor(line: ProductionToolProgramLine): ToolHolder | undefined {
		if (!line.toolHolderId) return undefined;
		return this.toolHolders.find((t) => t.id === line.toolHolderId);
	}

	toolTypeName(t?: CuttingTool): string {
		if (!t) return "";
		return CuttingToolType[t.toolType] ?? "";
	}
}
